import styled from '@emotion/styled';
import { ButtonProps, message, Spin, Tooltip } from 'antd';
import Button from 'antd/es/button';
import Popconfirm from 'antd/es/popconfirm';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useCallback, useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { CancelOrder, handleBuyerCancellation } from 'services/api/refund';
import TikTokRefuse from 'components/Refund/TikTokRefuse';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { cx } from '@emotion/css';

const PopConfrimContent = styled.div`
  width: 260px;
`;

type BasicButtonProps = Omit<ButtonProps, 'onClick'>;
export interface CancelApprovalBtnsProps {
  platform: General.CaselessPlatformTunnel;
  region?: General.UpperCaseRegion;
  internalStoreId: string;
  externalStoreId: CancelOrder['externalStoreId'];
  orderId: CancelOrder['orderId'];
  onUpdate?: () => void;
  buttonProps?: BasicButtonProps | [BasicButtonProps | undefined, BasicButtonProps | undefined];
  operate?: Conversation.Trade.IReturnOperationType[];
}

const CancelApprovalBtns = (props: CancelApprovalBtnsProps) => {
  const { t } = useTranslate();

  const sysLang = useSystemLanguageValue();
  const [loading, setLoading] = useState(false);
  const {
    platform,
    region,
    internalStoreId,
    externalStoreId,
    orderId,
    onUpdate,
    buttonProps,
    operate,
  } = props;

  const [leftBtnProp, rightBtnProp] = useMemo(() => {
    return Array.isArray(buttonProps) ? buttonProps : [buttonProps, buttonProps];
  }, [buttonProps]);

  const handleClick = useCallback(
    (isAccept: boolean) => {
      setLoading(true);
      handleBuyerCancellation({
        externalStoreId,
        orderId,
        platform,
        isAccept,
      })
        .then(() => {
          onUpdate && onUpdate();
          message.success(t('common.feedback.operationSuccess'));
        })
        .catch((err) => {
          if (err?.response?.data?.code === 1029) {
            // 没有权限
            try {
              const json = JSON.parse(err.response.data.msg);
              const msg = json[sysLang];
              message.error(msg);
            } catch (error) {
              message.error(t('common.feedback.operationFailedPleaseRetry'));
            }
          } else if (err?.response?.data?.code === 1028) {
            // 已由其他人处理
            message.error(t('common.error.alreadyProcessedByOthers'));
            onUpdate && onUpdate();
          } else {
            message.error(t('common.feedback.operationFailedPleaseRetry'));
          }
        })
        .finally(() => setLoading(false));
    },
    [externalStoreId, onUpdate, orderId, platform, sysLang, t],
  );

  let confirmUI;
  let rejectUI;
  if (platform === 'SHOPEE') {
    const disabled = !operate?.includes('AGREE');
    const btn = (
      <Button
        type="link"
        {...leftBtnProp}
        className={cx('!mb-1', leftBtnProp?.className)}
        disabled={disabled}
      >
        <span className={disabled ? '!text-content-100' : undefined}>
          {t('scene.orderWorkspace.cancelOrderTab.list.action.accept')}
        </span>
      </Button>
    );
    confirmUI = (
      <Popconfirm
        disabled={disabled}
        title={
          <PopConfrimContent>
            {t('scene.orderWorkspace.cancelOrderTab.dialog.acceptConfirm')}
          </PopConfrimContent>
        }
        onConfirm={() => handleClick(true)}
      >
        {disabled ? (
          <Tooltip title={t('scene.orderWorkspace.cancelOrderTab.tooltip.acceptNotAllowed')}>
            {btn}
          </Tooltip>
        ) : (
          btn
        )}
      </Popconfirm>
    );

    if (operate?.includes('REFUSE')) {
      rejectUI = (
        <Popconfirm
          title={
            <PopConfrimContent>
              {t('scene.orderWorkspace.cancelOrderTab.dialog.rejectConfirm')}
            </PopConfrimContent>
          }
          onConfirm={() => handleClick(false)}
        >
          <Button type="link" {...rightBtnProp} className={cx('!mb-1', rightBtnProp?.className)}>
            {t('scene.orderWorkspace.returnRefundTab.list.action.reject')}
          </Button>
        </Popconfirm>
      );
    }
  } else if (isTiKTokUpperCase(platform)) {
    confirmUI = (
      <Button
        type="link"
        {...leftBtnProp}
        className={cx('!mb-1', leftBtnProp?.className)}
        onClick={() => handleClick(true)}
      >
        {t('scene.orderWorkspace.cancelOrderTab.list.action.accept')}
      </Button>
    );
    rejectUI = (
      <TikTokRefuse
        platform={platform}
        region={region}
        onRefused={onUpdate}
        internalStoreId={internalStoreId}
        externalStoreId={externalStoreId}
        externalTradeId={orderId}
        inCanceled
        btnProps={rightBtnProp}
      />
    );
  }

  return (
    <Spin spinning={loading}>
      {confirmUI}
      {rejectUI}
    </Spin>
  );
};
export default CancelApprovalBtns;
